import React, { useLayoutEffect, useRef, useState, forwardRef } from "react";
import { createPortal } from "react-dom";

type Props = {
  anchorRef: React.RefObject<HTMLElement | null>;
  open: boolean;
  children: React.ReactNode;
  className?: string;
};

export const DropdownPortal = forwardRef<HTMLUListElement, Props>(
  ({ anchorRef, open, children, className }, ref) => {
    const [style, setStyle] = useState<React.CSSProperties>({});
    const dropdownRef = useRef<HTMLUListElement>(null);

    useLayoutEffect(() => {
      if (!open || !anchorRef.current) return;

      const updatePosition = () => {
        if (anchorRef.current) {
          const rect = anchorRef.current.getBoundingClientRect();
          const dropdownHeight = dropdownRef.current?.offsetHeight || 200;
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          let positionStyle: React.CSSProperties;
          if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
            positionStyle = {
              position: "fixed",
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
              maxHeight: Math.min(spaceBelow - 8, 300),
              overflowY: "auto",
            };
          } else {
            positionStyle = {
              position: "fixed",
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
              bottom: window.innerHeight - rect.top + 4,
              maxHeight: Math.min(spaceAbove - 8, 300),
              overflowY: "auto",
            };
          }
          setStyle(positionStyle);
        }
      };

      updatePosition();

      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
      // eslint-disable-next-line
    }, [open, anchorRef]);

    // Добавляем ResizeObserver для пересчёта позиции при изменении размера dropdown
    React.useEffect(() => {
      if (!open || !dropdownRef.current) return;
      const ro = new window.ResizeObserver(() => {
        if (anchorRef.current) {
          const rect = anchorRef.current.getBoundingClientRect();
          const dropdownHeight = dropdownRef.current?.offsetHeight || 200;
          const spaceBelow = window.innerHeight - rect.bottom;
          const spaceAbove = rect.top;

          let positionStyle: React.CSSProperties;
          if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
            positionStyle = {
              position: "fixed",
              top: rect.bottom + 4,
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
              maxHeight: Math.min(spaceBelow - 8, 300),
              overflowY: "auto",
            };
          } else {
            positionStyle = {
              position: "fixed",
              left: rect.left,
              width: rect.width,
              zIndex: 9999,
              bottom: window.innerHeight - rect.top + 4,
              maxHeight: Math.min(spaceAbove - 8, 300),
              overflowY: "auto",
            };
          }
          setStyle(positionStyle);
        }
      });
      ro.observe(dropdownRef.current);
      return () => ro.disconnect();
    }, [open, anchorRef]);

    // Пробрасываем ref наружу
    React.useImperativeHandle(ref, () => dropdownRef.current as HTMLUListElement, []);

    if (!open) return null;

    return createPortal(
      <ul ref={dropdownRef} className={className} style={style}>
        {children}
      </ul>,
      document.body
    );
  }
); 